import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShop } from '../contexts/ShopContext';
import { useToast } from '../contexts/ToastContext';
import { useZalandoAccess } from '../hooks/useZalandoAccess';
import { supabase } from '../lib/supabase';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  TrashIcon,
  ArrowPathIcon,
  PlusIcon,
  LinkIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

interface OfferLink {
  id: string;
  merchant_id: string;
  provider_id: string;
  zalando_url: string;
  status: string;
  last_synced_at: string | null;
  created_at: string;
}

interface SyncStats {
  variants: number;
  total_stock: number;
  avg_price: number;
}

// The Zalando provider ID — all scraped products go under this provider
const ZALANDO_PROVIDER_ID = 'afaa7fe0-6c8e-453c-8356-2d182968947e';

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; classes: string; label: string }> = {
  pending: {
    icon: <ClockIcon className="w-3.5 h-3.5" />,
    classes: 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400',
    label: 'Pending',
  },
  synced: {
    icon: <CheckCircleIcon className="w-3.5 h-3.5" />,
    classes: 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400',
    label: 'Synced',
  },
  error: {
    icon: <ExclamationTriangleIcon className="w-3.5 h-3.5" />,
    classes: 'bg-red-500/10 border border-red-500/20 text-red-400',
    label: 'Error',
  },
};

const ZalandoIntegration: React.FC = () => {
  const { user } = useAuth();
  const { currentShopId } = useShop();
  const { hasAccess, loading: accessLoading } = useZalandoAccess(currentShopId);
  const toast = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState<OfferLink[]>([]);
  const [stats, setStats] = useState<Record<string, SyncStats>>({});
  // Form state
  const [newUrl, setNewUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user?.id || !currentShopId) return;
    try {
      setLoading(true);

      // Fetch offer links
      const { data: linkRows, error: linkErr } = await supabase
        .from('zalando_offer_links')
        .select('*')
        .eq('merchant_id', currentShopId)
        .order('created_at', { ascending: false });
      if (linkErr) throw linkErr;
      setLinks(linkRows || []);

      // Fetch sync stats for synced links
      const syncedLinks = (linkRows || []).filter((l) => l.status === 'synced');
      if (syncedLinks.length > 0) {
        const statsMap: Record<string, SyncStats> = {};

        for (const link of syncedLinks) {
          // Find product descriptions that came from this URL
          const { data: descs } = await supabase
            .from('product_descriptions')
            .select('id')
            .eq('provider_id', link.provider_id)
            .contains('metafields', { source_url: link.zalando_url });

          if (!descs || descs.length === 0) {
            statsMap[link.id] = { variants: 0, total_stock: 0, avg_price: 0 };
            continue;
          }

          const descIds = descs.map((d) => d.id);

          // Get variants + inventory for these products
          const { data: variants } = await supabase
            .from('product_variants')
            .select('id, product_inventory(quantity, price)')
            .in('product_description_id', descIds);

          let totalVariants = 0;
          let totalStock = 0;
          let totalPrice = 0;
          let priceCount = 0;

          for (const v of variants || []) {
            totalVariants++;
            const inv = Array.isArray(v.product_inventory)
              ? v.product_inventory[0]
              : v.product_inventory;
            if (inv) {
              totalStock += inv.quantity || 0;
              if (inv.price > 0) {
                totalPrice += Number(inv.price);
                priceCount++;
              }
            }
          }

          statsMap[link.id] = {
            variants: totalVariants,
            total_stock: totalStock,
            avg_price: priceCount > 0 ? Math.round((totalPrice / priceCount) * 100) / 100 : 0,
          };
        }

        setStats(statsMap);
      }
    } catch (err: any) {
      console.error('Failed to load Zalando data:', err);
      toast.error(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user?.id, currentShopId, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentShopId || !newUrl.trim()) return;

    // Basic URL validation
    if (!newUrl.includes('zalando.')) {
      toast.error('Please enter a valid Zalando URL');
      return;
    }

    try {
      setSubmitting(true);

      const { error: insertErr } = await supabase
        .from('zalando_offer_links')
        .insert({
          merchant_id: currentShopId,
          provider_id: ZALANDO_PROVIDER_ID,
          zalando_url: newUrl.trim(),
        });

      if (insertErr) throw insertErr;

      setNewUrl('');
      setShowForm(false);
      toast.success('Link added — syncing now…');
      await fetchData();

      // Trigger the scraper immediately so the link is processed right away
      supabase.functions.invoke('zalando-link-scraper', { method: 'POST', body: {} })
        .then(() => {
          // Poll until this link is no longer pending
          const poll = setInterval(async () => {
            const { data } = await supabase
              .from('zalando_offer_links')
              .select('status')
              .eq('merchant_id', currentShopId!)
              .eq('status', 'pending');
            if (!data?.length) {
              clearInterval(poll);
              fetchData();
            }
          }, 5000);
          setTimeout(() => clearInterval(poll), 300_000);
        })
        .catch(() => {});
    } catch (err: any) {
      toast.error(err.message || 'Failed to add link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (linkId: string) => {
    try {
      const { error: delErr } = await supabase
        .from('zalando_offer_links')
        .delete()
        .eq('id', linkId);
      if (delErr) throw delErr;
      setLinks((prev) => prev.filter((l) => l.id !== linkId));
      toast.success('Link removed');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete link');
    }
  };

  const handleRequeue = async (linkId: string) => {
    try {
      const { error: updateErr } = await supabase
        .from('zalando_offer_links')
        .update({ status: 'pending' })
        .eq('id', linkId);
      if (updateErr) throw updateErr;
      setLinks((prev) =>
        prev.map((l) => (l.id === linkId ? { ...l, status: 'pending' } : l))
      );
      toast.success('Link re-queued');
    } catch (err: any) {
      toast.error(err.message || 'Failed to re-queue link');
    }
  };

  const handleSyncAll = async () => {
    if (syncing) return;
    try {
      setSyncing(true);

      // Set all links to pending
      const { error: updateErr } = await supabase
        .from('zalando_offer_links')
        .update({ status: 'pending' })
        .eq('merchant_id', currentShopId!)
        .in('status', ['synced', 'error']);
      if (updateErr) throw updateErr;

      setLinks((prev) => prev.map((l) => ({ ...l, status: 'pending' })));

      // Trigger the scraper (responds immediately, processes in background)
      await supabase.functions.invoke('zalando-link-scraper', { method: 'POST', body: {} });
      toast.success('Sync started — links will update shortly');

      // Poll for updates
      const poll = setInterval(async () => {
        const { data } = await supabase
          .from('zalando_offer_links')
          .select('status')
          .eq('merchant_id', currentShopId!)
          .eq('status', 'pending');
        if (!data?.length) {
          clearInterval(poll);
          setSyncing(false);
          fetchData();
        }
      }, 5000);

      // Stop polling after 5 minutes max
      setTimeout(() => {
        clearInterval(poll);
        setSyncing(false);
        fetchData();
      }, 300_000);
    } catch (err: any) {
      setSyncing(false);
      toast.error(err.message || 'Failed to start sync');
    }
  };

  const truncateUrl = (url: string, max = 55) => {
    if (url.length <= max) return url;
    return url.slice(0, max) + '…';
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const syncedCount = links.filter((l) => l.status === 'synced').length;
  const pendingCount = links.filter((l) => l.status === 'pending').length;
  const errorCount = links.filter((l) => l.status === 'error').length;


  if (accessLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/40 text-sm">Loading Zalando links…</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
      {/* Stats Row */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="grid grid-cols-3 gap-4 mb-6"
      >
        <div className="glass-card rounded-2xl p-5">
          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Total Links</p>
          <p className="font-heading font-bold text-white text-2xl">{links.length}</p>
        </div>
        <div className="glass-card rounded-2xl p-5">
          <p className="text-emerald-400/60 text-xs uppercase tracking-wider mb-1">Synced</p>
          <p className="font-heading font-bold text-emerald-400 text-2xl">{syncedCount}</p>
        </div>
        <div className="glass-card rounded-2xl p-5">
          <p className="text-red-400/60 text-xs uppercase tracking-wider mb-1">Errors</p>
          <p className="font-heading font-bold text-red-400 text-2xl">{errorCount}</p>
        </div>
      </motion.div>

      {/* Add Link Form */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="mb-6"
      >
        {!showForm ? (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm hover:bg-orange-500/20 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add Zalando URL
            </button>
            {links.length > 0 && (
              <button
                onClick={handleSyncAll}
                disabled={syncing || pendingCount === links.length}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing…' : 'Sync Now'}
              </button>
            )}
          </div>
        ) : (
          <form onSubmit={handleAddLink} className="glass-card rounded-2xl p-5">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="block text-xs text-white/40 mb-1.5">Zalando Product URL</label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                  <input
                    type="url"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder="https://www.zalando.de/product-name.html"
                    className="w-full pl-9 pr-3 py-2 border border-white/15 rounded-lg text-white text-sm bg-transparent focus:border-orange-500/50 focus:outline-none"
                    required
                  />
                </div>
              </div>
              <div className="flex items-end gap-2">
                <button
                  type="submit"
                  disabled={submitting || !newUrl.trim()}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? 'Adding…' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setNewUrl('');
                  }}
                  className="px-4 py-2 border border-white/15 text-white/50 rounded-lg text-sm hover:text-white hover:border-white/30 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}
      </motion.div>

      {/* Links Table */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="glass-card rounded-2xl overflow-hidden"
      >
        {links.length === 0 ? (
          <div className="p-12 text-center">
            <GlobeAltIcon className="w-12 h-12 text-white/15 mx-auto mb-4" />
            <p className="text-white/40 text-sm mb-2">No Zalando links yet</p>
            <p className="text-white/25 text-xs">
              Add a Zalando product URL to start importing products
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left text-xs text-white/40 font-medium px-5 py-3 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="text-left text-xs text-white/40 font-medium px-5 py-3 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right text-xs text-white/40 font-medium px-5 py-3 uppercase tracking-wider">
                    Variants
                  </th>
                  <th className="text-right text-xs text-white/40 font-medium px-5 py-3 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="text-right text-xs text-white/40 font-medium px-5 py-3 uppercase tracking-wider">
                    Avg Price
                  </th>
                  <th className="text-left text-xs text-white/40 font-medium px-5 py-3 uppercase tracking-wider">
                    Last Sync
                  </th>
                  <th className="text-right text-xs text-white/40 font-medium px-5 py-3 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {links.map((link, i) => {
                  const sc = STATUS_CONFIG[link.status] || STATUS_CONFIG.pending;
                  const st = stats[link.id];

                  return (
                    <motion.tr
                      key={link.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.03 }}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-5 py-3.5">
                        <a
                          href={link.zalando_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-white/70 hover:text-orange-400 transition-colors"
                          title={link.zalando_url}
                        >
                          {truncateUrl(link.zalando_url)}
                        </a>
                      </td>
                      <td className="px-5 py-3.5">
                        <span
                          className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${sc.classes}`}
                        >
                          {sc.icon}
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-sm text-white/50">
                          {st ? st.variants : '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-sm text-white/50">
                          {st ? st.total_stock : '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-sm text-white/50">
                          {st && st.avg_price > 0 ? `€${st.avg_price}` : '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-xs text-white/35">
                          {formatDate(link.last_synced_at)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {link.status !== 'pending' && (
                            <button
                              onClick={() => handleRequeue(link.id)}
                              title="Re-queue for sync"
                              className="p-1.5 rounded-lg text-white/30 hover:text-orange-400 hover:bg-orange-500/10 transition-colors"
                            >
                              <ArrowPathIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(link.id)}
                            title="Delete link"
                            className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ZalandoIntegration;
